'use client';

import classnames from 'classnames';

import { useStableValue } from '../chart-data';
import {
  seriesColor,
  toLegendLabels,
  tooltipOptions,
  useChartTheme,
  xAxisOptions,
  yAxisOptions,
} from '../chart-theme';
import { Legend } from '../layout/legend/Legend';
import { useChart } from '../useChart';
import type { BarChartProps } from './BarChart.types';

import styles from './BarChart.module.scss';

function BarChart({
  style,
  className,
  labels: labelsProp,
  dataset: datasetProp,
  stacked = false,
  label,
  testId = 'ui-bar-chart',
}: BarChartProps) {
  const theme = useChartTheme();
  const dataset = useStableValue(datasetProp);
  const labels = useStableValue(labelsProp);

  const canvasRef = useChart<'bar'>(
    () => ({
      type: 'bar',
      data: {
        labels: labels,
        datasets: dataset.map((data, index) => {
          const color = seriesColor(data.color, index);
          return {
            data: data.values,
            label: data.label,
            borderWidth: 1,
            borderColor: color,
            backgroundColor: color,
            barThickness: 10,
          };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            ...tooltipOptions(theme),
            callbacks: {
              label: (context) => {
                const datasets = Array.from(context.chart.data.datasets);
                const values: string[] = [];
                datasets.forEach((dataset) => {
                  values.push(
                    dataset.data[context.dataIndex]?.toString() ?? '',
                  );
                });
                return values.join(' ');
              },
              labelTextColor: () => theme.primary,
            },
          },
        },
        scales: {
          x: {
            ...xAxisOptions(theme),
            stacked: stacked,
          },
          y: {
            ...yAxisOptions(theme),
            stacked: stacked,
          },
        },
      },
    }),
    [dataset, labels, stacked, theme],
  );

  return (
    <div
      style={style}
      className={classnames(styles['bar-chart'], className)}
      data-testid={testId}
    >
      <div className={styles['kpi']}>
        <canvas ref={canvasRef} role="img" aria-label={label} />
      </div>
      <Legend labels={toLegendLabels(dataset)} />
    </div>
  );
}

export { BarChart };
